/**
 * PrivacyDataSection — DSGVO-Betroffenenrechte für den Kunden:
 *   - Art. 20: Datenexport (lädt alle eigenen Daten als JSON herunter)
 *   - Art. 17: Konto-/Workspace-Löschung (mit ausdrücklicher Bestätigung)
 *
 * Beide Aktionen sind serverseitig streng workspace-isoliert.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, Trash2, Loader2, ShieldCheck } from "lucide-react";
import { useLanguage } from "@/lib/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Bestätigungswort ist server- UND client-seitig fest "LÖSCHEN" (server.py:6274,
// 6290 — sprachunabhängig geprüft). NICHT übersetzen: ein "DELETE" würde die
// Backend-Prüfung nie bestehen. Das englische Drehbuch (docs/APP_REVIEW_META_VIDEO_
// SCRIPT.md) tippt bewusst ebenfalls "LÖSCHEN", unabhängig von der UI-Sprache.
const BESTAETIGUNGSWORT = "LÖSCHEN";

export default function PrivacyDataSection() {
  const { t } = useLanguage();
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [showDelete, setShowDelete] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/account/export`);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `foerderfuchs-datenexport-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(t("Datenexport heruntergeladen.", "Data export downloaded."));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("Export fehlgeschlagen.", "Export failed."));
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.post(`${API}/account/delete`, { confirm });
      toast.success(t("Deine Daten wurden gelöscht.", "Your data has been deleted."));
      setShowDelete(false);
      setConfirm("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("Löschung fehlgeschlagen.", "Deletion failed."));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-stone-200 p-6 space-y-4" data-testid="privacy-data-section">
      <div className="flex items-center gap-2">
        <ShieldCheck size={20} className="text-stone-500" />
        <h2 className="font-heading font-bold text-lg text-stone-800">{t("Datenschutz & meine Daten", "Privacy & my data")}</h2>
      </div>
      <p className="text-sm text-stone-500">
        {t(
          "Du kannst jederzeit eine Kopie all deiner Daten herunterladen oder dein Konto mit allen Inhalten unwiderruflich löschen (DSGVO Art. 20 & Art. 17).",
          "You can download a copy of all your data at any time, or permanently delete your account along with all its content (GDPR Art. 20 & Art. 17).",
        )}
      </p>

      <div className="flex flex-wrap gap-3">
        <button
          onClick={handleExport}
          disabled={exporting}
          data-testid="dsgvo-export-btn"
          className="flex items-center gap-2 px-4 py-2 rounded-lg border border-stone-200 text-sm font-medium text-stone-700 hover:bg-stone-50 disabled:opacity-60"
        >
          {exporting ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
          {t("Daten exportieren (JSON)", "Export data (JSON)")}
        </button>

        {!showDelete ? (
          <button
            onClick={() => setShowDelete(true)}
            data-testid="dsgvo-delete-open-btn"
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-red-200 text-sm font-medium text-red-600 hover:bg-red-50"
          >
            <Trash2 size={16} />
            {t("Konto & Daten löschen", "Delete account & data")}
          </button>
        ) : null}
      </div>

      {showDelete && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 space-y-3">
          <p className="text-sm text-red-700">
            {t(
              "Dadurch werden alle deine Inhalte, Pläne, Medien und Kanal-Verbindungen unwiderruflich gelöscht. Tippe zur Bestätigung",
              "This will permanently delete all your content, plans, media, and channel connections. Type to confirm",
            )}{" "}
            <strong>{BESTAETIGUNGSWORT}</strong>:
          </p>
          <input
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder={BESTAETIGUNGSWORT}
            data-testid="dsgvo-delete-confirm-input"
            className="w-full max-w-xs px-3 py-2 rounded-lg border border-red-200 focus:border-red-400 outline-none text-sm"
          />
          <div className="flex gap-2">
            <button
              onClick={handleDelete}
              disabled={deleting || confirm.trim().toUpperCase() !== BESTAETIGUNGSWORT}
              data-testid="dsgvo-delete-confirm-btn"
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-50"
            >
              {deleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
              {t("Endgültig löschen", "Permanently delete")}
            </button>
            <button
              onClick={() => { setShowDelete(false); setConfirm(""); }}
              className="px-4 py-2 rounded-lg border border-stone-200 text-sm text-stone-600 hover:bg-stone-50"
            >
              {t("Abbrechen", "Cancel")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
